import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMemo, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { ColumnDef } from "@tanstack/react-table";
import { OrderDocumentType } from "@/lib/validations/schema";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { DataTable } from "components/Table/DataTable";
import { DocumentItem, Customers as TypeCustomer } from "pages/GenericOrderDetailsPage/types/order";
import DocumentReview from "pages/OrderDetails/verifydocuments";
import KycReviewModal from "../../components/KycReviewModal";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { approveDocuments, approveKyc, getKycLogs, requestDocument, reuploadDocument, submitDocument, updateLrsStatus } from "pages/GenericOrderDetailsPage/apis";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { Spinner } from "components";
import RequestOrderDoc from "pages/OrderDetails/request_doc";
import EditProduct from "pages/GenericOrderDetailsPage/components/edit-product-form";
import { convertFileToBase64 } from "pages/CreateRemit/documentation";
import { all_doc_List } from "pages/GenericOrderDetailsPage/utils";
import { RequestDocumenReqType } from "pages/GenericOrderDetailsPage/types/type.api";
import { Download, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { handleDownloadViaURL } from "utils";
import { AlertDialog, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";

const subTabsData = [
    {
        id: 'documents',
        label: 'Documents',
        value: 'documents',
        enabled: true
    },
    // {
    //     id: 'kyc',
    //     label: 'KYC',
    //     value: 'kyc',
    //     enabled: true
    // },
    // {
    //     id: 'lrs',
    //     label: 'LRS',
    //     value: 'lrs',
    //     enabled: true
    // }
];

export default function Customers({isDeliveryFulfilled, isLrsSigned, customers, order_documents, selectedCustomerId, onCustomerTabClick, documentsButtonState, paymentButtonState }: {
    customers: TypeCustomer;
    isDeliveryFulfilled: boolean;
    selectedCustomerId?: number | null;
    onCustomerTabClick?: (customerId: number) => void;
    order_documents: DocumentItem[];
    documentsButtonState: {
        disabled: boolean;
        visible: boolean;
    },
    paymentButtonState: {
        disabled: boolean;
        visible: boolean;
    }
    isLrsSigned: boolean;
}) {
    const [activeTab, setActiveTab] = useState<string>('');
    const [subTabs, setSubTabs] = useState<string>('documents')
    const [open, setOpen] = useState(false);
    const [docIndex, setDocIndex] = useState(0);
    const [kycCustomerId, setKycCustomerId] = useState<number | null>(null);
    const [kycModalOpen, setKycModalOpen] = useState(false);
    const orderId = useParams().id as string;
    const queryClient = useQueryClient();
    const [activeCustomer, setActiveCustomer] = useState<number>(0)
    const [isSignedLrsOpen, setIsSignedLrsOpen] = useState(false);
    const [isLrsChecked, setIsLrsChecked] = useState(false);

    // const preDefinedOrderDocuments: OrderDocumentType[] = [
    //     {

    //     }
    // ]

    const orderDocumentsToPassInReviewModal = useMemo(() => {
        const base = order_documents
            ?.filter((doc: any) => doc?.created_by_cx === activeCustomer)
            .map((doc) => ({
                id: doc.id as string,
                name: doc.doc_name as string,
                type: doc.document?.type as 'pdf' | 'image',
                status: doc.status as "Unverified" | "Verified" | "Requested",
                url: doc.document?.url as string
            })) || [];

        return base;
    }, [order_documents, activeCustomer]);


    const {data: kycLogs} = useQuery({
        queryKey: ['kycLogs', {customers_id: activeCustomer}],
        queryFn: () => getKycLogs({customers_id: activeCustomer}),
    })    

    // Separate KYC documents for the KYC modal
    const kycDocuments = useMemo(() => {

        const currentCustomer = customers?.product_customer?.find((p) => p?.created_for === activeCustomer);
        const cd = (currentCustomer?.customer_detail || {}) as Record<string, any>;
        const kycStatus = (cd?.kyc_status as string) || '';
        const isActive = String(kycStatus).toLowerCase() === 'active';

        const kycDocs: { id: string; name: string; url: { url: string }; status: "Unverified" | "Verified" | "Requested" }[] = [];
        if ((cd as any)?.pan_soft_copy) {
            kycDocs.push({ id: 'kyc_pan', name: 'PAN Soft Copy', url: cd.pan_soft_copy, status: isActive ? "Verified" : "Unverified" });
        }
        if ((cd as any)?.passport_front) {
            kycDocs.push({ id: 'kyc_passport_front', name: 'Passport Front', url: cd.passport_front, status: isActive ? "Verified" : "Unverified" });
        }
        if ((cd as any)?.passport_back) {
            kycDocs.push({ id: 'kyc_passport_back', name: 'Passport Back', url: cd.passport_back, status: isActive ? "Verified" : "Unverified" });
        }
        return kycDocs;
    }, [customers, activeCustomer, kycCustomerId]);

    // group by customer name
    const groupedTabsData: Record<string, any> = customers?.product_customer?.reduce((acc: any, tab) => {
        if (tab?.status === 'Reject') return acc;
        const customerName = tab.customer_detail?.full_name;
        if (!acc[customerName]) {
            acc[customerName] = {
                id: tab.id,
                label: customerName,
                value: customerName,
                data: [tab],
                enabled: true,
                customerId: tab.created_for,
            };
        } else {
            acc[customerName] = {
                ...acc[customerName],
                data: [...acc[customerName].data, tab],
            };
        }
        return acc;
    }, {} as Record<string, any>);
    const tabsdata: any[] = Object.values(groupedTabsData);

    // set active tab as first tab from tabData or selectedCustomerId
    useEffect(() => {
        if (selectedCustomerId) {
            const found = tabsdata.find((tab: any) => tab.customerId === selectedCustomerId);

            if (found) {
                setActiveTab(found.value);
                setActiveCustomer(found?.customerId)
            }
        } else if (activeTab === '' && tabsdata.length > 0) {
            setActiveTab((tabsdata[0] as any)?.value);
            setActiveCustomer(tabsdata[0]?.customerId)
        }
    }, [selectedCustomerId, tabsdata]);

    const onTabChange = (value: string) => {
        setActiveTab(value);
    };
    const { mutate, isLoading } = useMutation({
        mutationFn: approveDocuments,
        onSuccess: (_data) => {
            toast.success('Documents approved successfully');
            queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error approving documents');
        }
    })

    const { mutate: reuploadMutation, isLoading: reuploadLoading, mutateAsync } = useMutation({
        mutationFn: reuploadDocument,
        onSuccess: () => {
            toast.success('Documents approved successfully');
            queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error approving documents');
        }
    })

    const onApprove = async (id: string, _name: string, file: File | null) => {
        let base64: Base64URLString;
        const matchedDoc = all_doc_List?.find(doc => String(doc.id) === id);
        const type = matchedDoc?.type || "personal";

        if (file) {
            base64 = await convertFileToBase64(file) as Base64URLString
            reuploadMutation({
                documents_id: id,
                doc: base64,
                order_details_id: Number(orderId),
                customers_id: activeCustomer,
                type: type,
            });
        } else {
            mutate({
                order_details_id: orderId,
                document_list: [
                    {
                        id: id,
                        status: 'verified'
                    }
                ]
            })
        }
    };
    const onReUpload = async (id: string, file: string, _name: string) => {
        if (file) {
            reuploadMutation({
                documents_id: id,
                doc: file,
                order_details_id: Number(orderId),
                customers_id: activeCustomer,
                type: 'personal'
            })
        }
    }


    const { mutate: requestDocMutation, isLoading: RequestDocLoading } = useMutation({
        mutationFn: requestDocument,
        onSuccess: () => {
            queryClient.invalidateQueries(['orderDetails'])
            toast.success('Document requested successfully');
        },
        onError: () => { }
    })

    const { mutate: submitDocMutation, isLoading: submitDocLoading } = useMutation({
        mutationFn: submitDocument,
        onSuccess: () => {
            toast.success('Documents submitted successfully');
            queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error submitting documents');
        }
    })

    const handSubmitDocuments = (order_id: number) => {
        submitDocMutation({
            order_details_id: order_id
        })
    }

    const handleRequestDocument = (id: string | number) => {
        const matchedDoc = all_doc_List?.find(doc => doc.id === id);
        const type = matchedDoc?.type || 'personal';
        let dataToSend = {};
        if (typeof id === 'string') {
            dataToSend = {
                type: type as string,
                req_for: Number(activeCustomer),
                documents_id: id as string
            }
        }
        else {
            dataToSend = {
                type: type as string,
                req_for: Number(activeCustomer),
                doc_list_id: id as number
            }
        }

        requestDocMutation({
            order_id: Number(orderId),
            req_doc: [dataToSend as RequestDocumenReqType['req_doc'][number]]
        });
    };

    // removed unused mutation to avoid linter warnings

    const handleAddAdditionalDocument = async (documents: {
        file: File | null;
        name: string;
        id: string;
    }[]) => {
        const allDocs = []
        for (const doc of documents) {
            let base64: Base64URLString = '';
            if (doc.file) {
                base64 = await convertFileToBase64(doc.file) as Base64URLString
            }
            allDocs.push({
                name: doc.name,
                type: 'personal',
                file: base64 || '',
            })
        }
        return mutateAsync({
            documents_id: '',
            doc: '',
            order_details_id: Number(orderId),
            additional_doc: allDocs,
            type: 'personal',
            customers_id: activeCustomer,
        })
    }

    const { mutate: handleKycMutation, isLoading: KYCLoading } = useMutation({
        mutationFn: approveKyc,
        onSuccess: () => {
            toast.success('KYC approved successfully');
            queryClient.invalidateQueries(['orderDetails']);
            setKycModalOpen(false);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'KYC approval failed');
        }
    })

    const { mutate: handleLrsMutation, isLoading: LRSLoading } = useMutation({
        mutationFn: updateLrsStatus,
        onSuccess: () => {
            setIsLrsChecked(false);
            setIsSignedLrsOpen(false);
            toast.success('LRS approved successfully');
            queryClient.invalidateQueries(['orderDetails']);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'LRS approval failed');
        }
    })

    const onApproveKyc = () => {
        handleKycMutation({
            customers_id: Number(activeCustomer),
        });
    }
    return (
        <>
            {
                open && (
                    <DocumentReview
                        index={docIndex}
                        isOpen={open}
                        setIsOpen={setOpen}
                        onReUpload={onReUpload}
                        refetchOrderDetails={() => { }}
                        documents={orderDocumentsToPassInReviewModal}
                        onApprove={onApprove}
                        onRequest={handleRequestDocument}
                        onSubmit={handSubmitDocuments}
                        handleAddAdditionalDocument={handleAddAdditionalDocument}
                    />
                )
            }
            <KycReviewModal
                isOpen={kycModalOpen}
                setIsOpen={(val) => { if (!val) setKycCustomerId(null); setKycModalOpen(val); }}
                documents={kycDocuments}
                orderId={orderId}
                customerId={activeCustomer}
                onApprove={onApproveKyc}
                isLoading={KYCLoading}
            />
            <AlertDialog open={isSignedLrsOpen} onOpenChange={setIsSignedLrsOpen}>
                <AlertDialogPortal>
                    {isLoading && <Spinner />}
                    <AlertDialogOverlay className="bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                    <AlertDialogContent
                        className={cn(
                            "sm:max-w-[500px] p-0 rounded-lg gap-1 bg-white-A700 animate-contentShow",
                            ''
                        )}
                    >
                        <AlertDialogHeader className="flex text-left gap-4 p-4 border-b">
                            <AlertDialogTitle>Update Sign LRS</AlertDialogTitle>
                        </AlertDialogHeader>
                        <div className="">
                            <form className="" onSubmit={e => {
                                e.preventDefault();
                                handleLrsMutation({ order_id: Number(orderId) });
                            }}>
                                <div
                                    className={cn(
                                        "space-y-4 h-full px-10 py-8 overflow-auto max-h-[700px]",
                                        ''
                                    )}
                                >
                                    <div className="flex items-center rounded-lg space-x-2 bg-[#F9F9F9] p-4">
                                        <label
                                            htmlFor="lrs-signed"
                                            className="text-sm flex items-start gap-2 font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                        >
                                            <Checkbox id="lrs-signed" className="border-[#333333]" checked={isLrsChecked} onCheckedChange={val=>setIsLrsChecked(!!val)} />
                                            <p className="text-[#666666] tracking-[-2%] leading-[140%]">I hereby declare that the customer has signed the LRS document offline, and I have the original signed documents on record.</p>
                                        </label>
                                    </div>
                                </div>
                                <AlertDialogFooter className="flex bg-[#F9F9F9] rounded-b-xl gap-2 justify-between items-center flex-row p-4">
                                    <Button
                                        className="text-blue-700 border-0 w-[100px]"
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setIsSignedLrsOpen(false);
                                            setIsLrsChecked(false);
                                        }}
                                    >
                                        Close
                                    </Button>
                                    <Button
                                        disabled={!isLrsChecked}
                                        className="bg-blue-700 border border-blue-700 text-white-A700 w-[100px]"
                                        type="submit"
                                    >
                                        Update
                                    </Button>
                                </AlertDialogFooter>
                            </form>
                        </div>
                    </AlertDialogContent>
                </AlertDialogPortal>
            </AlertDialog> 
            {
                (LRSLoading || isLoading || reuploadLoading || RequestDocLoading || submitDocLoading || KYCLoading) && <Spinner />
            }

            <Tabs value={activeTab} onValueChange={onTabChange} defaultValue="customers" className="h-full flex flex-col gap-5">
                <TabsList className="flex items-center gap-5 w-fit h-full rounded-[12px] p-0">
                    {tabsdata?.map((tab: any) => {
                        return (
                            <TabsTrigger
                                key={tab.id}
                                value={tab.value}
                                disabled={!tab.enabled}
                                onClick={() => {
                                    onCustomerTabClick && onCustomerTabClick(tab.customerId);
                                    setActiveCustomer(tab.customerId);
                                }}
                                className={`px-4 min-w-[230px] w-full py-3.5 border rounded-[12px] text-black-label capitalize border-transparent text-sm ${tab.value === activeTab
                                    ? String(tab.data[0]?.customer_detail?.kyc_status || '').toLowerCase() === 'active'
                                        ? 'border-blue-700 text-blue-700 shadow bg-[#EFF4FF]'
                                        : 'border-blue-700 text-[#FF0000] shadow bg-[#EFF4FF]'
                                    : ''
                                    }`}
                            >
                                <div className="flex gap-2 items-center justify-between w-full">
                                    <div className="flex gap-2 items-center">
                                        {tab.label}
                                        {/* Show warning icon when delivery is done, Sign LRS is available, but LRS is not signed */}
                                        {/* {String(tab.data[0]?.customer_detail?.kyc_status || '').toLowerCase() === 'active' && isDeliveryFulfilled && !isLrsSigned && (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-4 h-4"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="red"
                                                strokeWidth="2"
                                            >
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="8" x2="12" y2="12" />
                                                <circle cx="12" cy="16" r="1" fill="red" />
                                            </svg>
                                        )} */}
                                        {/* Show checkmark when LRS is signed */}
                                        {String(tab.data[0]?.customer_detail?.kyc_status || '').toLowerCase() === 'active' && isLrsSigned && (
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8 0C3.592 0 0 3.592 0 8C0 12.408 3.592 16 8 16C12.408 16 16 12.408 16 8C16 3.592 12.408 0 8 0ZM11.824 6.16L7.288 10.696C7.176 10.808 7.024 10.872 6.864 10.872C6.704 10.872 6.552 10.808 6.44 10.696L4.176 8.432C3.944 8.2 3.944 7.816 4.176 7.584C4.408 7.352 4.792 7.352 5.024 7.584L6.864 9.424L10.976 5.312C11.208 5.08 11.592 5.08 11.824 5.312C12.056 5.544 12.056 5.92 11.824 6.16Z" fill="#3FB73D" />
                                            </svg>
                                        )}
                                    </div>

                                    {/* Show exclamation if KYC is NOT active */}
                                    {String(tab.data[0]?.customer_detail?.kyc_status || '').toLowerCase() !== 'active' ? (
                                        <Button disabled={documentsButtonState?.disabled} variant='ghost' onClick={() => {
                                            setKycCustomerId(tab.customerId);
                                            setActiveCustomer(tab.customerId);
                                            setKycModalOpen(true);
                                        }} className="text-red-400 font-semibold py-0 h-fit tracking-[-2%] leading-4 underline underline-offset-1">Review KYC</Button>
                                    )
                                        :
                                        (isLrsSigned || !isDeliveryFulfilled ) ? null :
                                            <Button disabled={documentsButtonState?.disabled} variant='ghost' onClick={() => {
                                                setIsSignedLrsOpen(true);
                                            }} className="text-red-400 font-semibold py-0 h-fit tracking-[-2%] leading-4 underline underline-offset-1">Sign LRS</Button>
                                    }
                                </div>
                            </TabsTrigger>

                        );
                    })}
                    {/* <TabsTrigger
                        value=""
                        disabled
                        className='px-4 py-3.5 border rounded-[12px] capitalize border-transparent text-sm border-blue-700 text-blue-700 shadow bg-[#EFF4FF]'
                    >
                        <button className="flex gap-2 items-center justify-between w-fit">
                            <Plus className="w-5 h-5 font-bold" />
                        </button>
                    </TabsTrigger> */}
                </TabsList>
                <div className="border-b border-[1.5px] w-full bg-gray-700" />

                {tabsdata?.map((tab: any) => {
                    let combinedData: any[] = []
                    combinedData = [...order_documents];
                    let groupedDocuments = combinedData?.reduce((acc: any, doc: any) => {
                        const docType = doc.type_of_doc || 'others';
                        if (!acc[docType]) {
                            acc[docType] = [];
                        }
                        acc[docType].push(doc);
                        return acc;
                    }, {} as Record<string, any>);
                    // add an all tab
                    const allDocuments = Object.values(groupedDocuments).flat();
                    groupedDocuments['all'] = allDocuments;
                    const allDocumentsData = groupedDocuments['all'];
                    delete groupedDocuments['all'];
                    groupedDocuments = {
                        'all': allDocumentsData,
                        ...groupedDocuments
                    };

                    const kycStatus = String(tab.data[0]?.customer_detail?.kyc_status || '');

                    return (
                        <TabsContent
                            key={tab.id}
                            value={tab.value}
                            className="h-full w-full m-0 gap-8 flex"
                        >
                            <Tabs value={subTabs} onValueChange={(value) => setSubTabs(value)} defaultValue="documents" className="h-full flex gap-8 w-full">
                                <TabsList className="flex p-1 items-start justify-start flex-col gap-5 h-full rounded-[12px] bg-[#FAFAFA]">
                                    {
                                        subTabsData.map((subTab) => (
                                            <TabsTrigger key={subTab.id} className={cn('px-4 w-[150px] items-start justify-between gap-2 py-3.5 border rounded-[12px] capitalize border-transparent text-sm  ', subTab?.value === subTabs && 'border-blue-700 text-blue-700 shadow bg-[#EFF4FF]')} value={subTab.value}>
                                                {subTab.label}
                                                {
                                                    subTab.value === 'kyc' &&
                                                    (String(tab.data[0]?.customer_detail?.kyc_status || '').toLowerCase() !== 'active' ?
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="red"
                                                        strokeWidth="2"
                                                    >
                                                        <circle cx="12" cy="12" r="10" />
                                                        <line x1="12" y1="8" x2="12" y2="12" />
                                                        <circle cx="12" cy="16" r="1" fill="red" />
                                                    </svg>:
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M8 0C3.592 0 0 3.592 0 8C0 12.408 3.592 16 8 16C12.408 16 16 12.408 16 8C16 3.592 12.408 0 8 0ZM11.824 6.16L7.288 10.696C7.176 10.808 7.024 10.872 6.864 10.872C6.704 10.872 6.552 10.808 6.44 10.696L4.176 8.432C3.944 8.2 3.944 7.816 4.176 7.584C4.408 7.352 4.792 7.352 5.024 7.584L6.864 9.424L10.976 5.312C11.208 5.08 11.592 5.08 11.824 5.312C12.056 5.544 12.056 5.92 11.824 6.16Z" fill="#3FB73D" />
                                                        </svg>
                                                    )
                                                }
                                                {
                                                    subTab.value === 'documents' && ( (groupedDocuments?.['all'] || [])?.length > 0 && groupedDocuments?.['all']?.every((doc: any) => doc?.status === 'verified') ?
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M8 0C3.592 0 0 3.592 0 8C0 12.408 3.592 16 8 16C12.408 16 16 12.408 16 8C16 3.592 12.408 0 8 0ZM11.824 6.16L7.288 10.696C7.176 10.808 7.024 10.872 6.864 10.872C6.704 10.872 6.552 10.808 6.44 10.696L4.176 8.432C3.944 8.2 3.944 7.816 4.176 7.584C4.408 7.352 4.792 7.352 5.024 7.584L6.864 9.424L10.976 5.312C11.208 5.08 11.592 5.08 11.824 5.312C12.056 5.544 12.056 5.92 11.824 6.16Z" fill="#3FB73D" />
                                                        </svg>
                                                        :
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="w-4 h-4"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="red"
                                                            strokeWidth="2"
                                                        >
                                                            <circle cx="12" cy="12" r="10" />
                                                            <line x1="12" y1="8" x2="12" y2="12" />
                                                            <circle cx="12" cy="16" r="1" fill="red" />
                                                        </svg>
                                                    )
                                                }
                                                {
                                                    subTab.value === 'lrs' &&
                                                    (!isLrsSigned ?
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="red"
                                                        strokeWidth="2"
                                                    >
                                                        <circle cx="12" cy="12" r="10" />
                                                        <line x1="12" y1="8" x2="12" y2="12" />
                                                        <circle cx="12" cy="16" r="1" fill="red" />
                                                    </svg>:
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M8 0C3.592 0 0 3.592 0 8C0 12.408 3.592 16 8 16C12.408 16 16 12.408 16 8C16 3.592 12.408 0 8 0ZM11.824 6.16L7.288 10.696C7.176 10.808 7.024 10.872 6.864 10.872C6.704 10.872 6.552 10.808 6.44 10.696L4.176 8.432C3.944 8.2 3.944 7.816 4.176 7.584C4.408 7.352 4.792 7.352 5.024 7.584L6.864 9.424L10.976 5.312C11.208 5.08 11.592 5.08 11.824 5.312C12.056 5.544 12.056 5.92 11.824 6.16Z" fill="#3FB73D" />
                                                        </svg>
                                                    )
                                                }
                                            </TabsTrigger>
                                        ))
                                    }
                                </TabsList>
                                <TabsContent className="w-full" value='documents'>
                                    <DocumentsAndLRS kycStatus={kycStatus} setDocIndex={setDocIndex} documentsButtonState={documentsButtonState} setOpen={(val) => { if (!val) { setKycCustomerId(null); } setOpen(val); }} documents={groupedDocuments} activeCustomer={activeCustomer} />
                                </TabsContent>
                                <TabsContent className="w-full" value='kyc'>
                                    <KycDocumentsTable documents={kycLogs}/>
                                </TabsContent>
                                <TabsContent value=''>

                                </TabsContent>
                            </Tabs>
                        </TabsContent>
                    );
                })}
            </Tabs>
        </>
    )
}

const CustomerSection = ({ name, phone, email, kycStatus, onVerifyKyc }: {
    name: string;
    phone: string;
    email: string;
    kycStatus?: string;
    onVerifyKyc?: () => void;
}) => {
    const isKycActive = String(kycStatus || '').toLowerCase() === 'active';


    return (
        <div className={`rounded-xl border py-4 px-8 ${isKycActive
            ? 'bg-[#F8FAFF] border-[#DDE9FF]'
            : 'bg-[#FFF8F8] border-[#FFD6D6]'
            }`}>
            <div className="flex justify-between gap-3 items-start">
                <h2 className={`flex flex-row items-center gap-2.5 text-xl font-semibold leading-[150%] capitalize ${isKycActive ? 'text-blue-700' : 'text-[#FF0000]'
                    }`}>{name}{!isKycActive && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="red"
                            strokeWidth="2"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <circle cx="12" cy="16" r="1" fill="red" />
                        </svg>
                    )}</h2>
                {String(kycStatus || '').toLowerCase() !== 'active' && (
                    <Button size="sm" variant="ghost" className={`border rounded-lg ${isKycActive
                        ? 'border-blue-700 text-blue-700'
                        : 'border-[#FF0000] text-[#FF0000]'
                        }`} onClick={onVerifyKyc}>
                        Verify KYC
                    </Button>
                )}
            </div>
            <div className="flex justify-between flex-col gap-3">
                <div className="flex gap-[22px] items-center">
                    <small className="flex gap-3 items-center text-sm font-normal leading-[160%] text-black-label">
                        <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.2219 12.0429L12.5639 11.7031L11.7696 10.9051L11.4291 11.2449L12.2219 12.0429ZM13.7106 11.5606L15.1439 12.3399L15.6801 11.3514L14.2476 10.5729L13.7106 11.5606ZM15.4191 13.8879L14.3541 14.9476L15.1469 15.7449L16.2119 14.6859L15.4191 13.8879ZM13.7046 15.2881C12.6171 15.3901 9.80463 15.2994 6.75813 12.2709L5.96463 13.0681C9.28863 16.3734 12.4529 16.5354 13.8096 16.4086L13.7046 15.2881ZM6.75813 12.2709C3.85488 9.38335 3.37338 6.9556 3.31338 5.90185L2.18988 5.9656C2.26488 7.2916 2.86113 9.9826 5.96463 13.0681L6.75813 12.2709ZM7.78938 7.63585L8.00463 7.42135L7.21263 6.6241L6.99738 6.83785L7.78938 7.63585ZM8.17563 4.6951L7.23063 3.4321L6.32988 4.1071L7.27488 5.36935L8.17563 4.6951ZM4.04988 3.15685L2.87238 4.32685L3.66588 5.12485L4.84263 3.95485L4.04988 3.15685ZM7.39338 7.23685C6.99588 6.83785 6.99588 6.83785 6.99588 6.83935H6.99438L6.99213 6.84235C6.95649 6.87829 6.92458 6.91774 6.89688 6.9601C6.85638 7.0201 6.81213 7.09885 6.77463 7.1986C6.68334 7.4561 6.66059 7.73291 6.70863 8.00185C6.80913 8.6506 7.25613 9.50785 8.40063 10.6464L9.19413 9.84835C8.12238 8.78335 7.86738 8.13535 7.82013 7.82935C7.79763 7.68385 7.82088 7.61185 7.82763 7.59535C7.83213 7.58535 7.83213 7.58385 7.82763 7.59085C7.82094 7.6012 7.81342 7.61099 7.80513 7.6201L7.79763 7.6276L7.79013 7.63435L7.39338 7.23685ZM8.40063 10.6464C9.54588 11.7849 10.4076 12.2289 11.0571 12.3279C11.3894 12.3789 11.6571 12.3384 11.8604 12.2626C11.9742 12.221 12.0806 12.1612 12.1754 12.0856L12.2129 12.0519L12.2181 12.0474L12.2204 12.0451L12.2211 12.0436C12.2211 12.0436 12.2219 12.0429 11.8251 11.6439C11.4276 11.2449 11.4299 11.2441 11.4299 11.2441L11.4314 11.2426L11.4329 11.2411L11.4374 11.2374L11.4449 11.2299L11.4734 11.2074C11.4804 11.2029 11.4786 11.2034 11.4681 11.2089C11.4494 11.2156 11.3759 11.2389 11.2281 11.2164C10.9176 11.1684 10.2651 10.9134 9.19413 9.84835L8.40063 10.6464ZM7.23063 3.43135C6.46563 2.41135 4.96263 2.24935 4.04988 3.15685L4.84263 3.95485C5.24163 3.5581 5.94963 3.59935 6.32988 4.1071L7.23063 3.43135ZM3.31413 5.9026C3.29913 5.6431 3.41838 5.3716 3.66588 5.1256L2.87163 4.3276C2.46888 4.7281 2.15163 5.2951 2.18988 5.9656L3.31413 5.9026ZM14.3541 14.9476C14.1486 15.1531 13.9266 15.2686 13.7054 15.2889L13.8096 16.4086C14.3609 16.3569 14.8116 16.0794 15.1476 15.7456L14.3541 14.9476ZM8.00463 7.42135C8.74338 6.6871 8.79813 5.52685 8.17638 4.69585L7.27563 5.3701C7.57788 5.77435 7.53288 6.3046 7.21188 6.62485L8.00463 7.42135ZM15.1446 12.3406C15.7574 12.6736 15.8526 13.4581 15.4199 13.8886L16.2134 14.6859C17.2184 13.6861 16.9086 12.0189 15.6809 11.3521L15.1446 12.3406ZM12.5639 11.7039C12.8519 11.4174 13.3154 11.3469 13.7114 11.5614L14.2484 10.5736C13.4354 10.1311 12.4274 10.2534 11.7704 10.9059L12.5639 11.7039Z" fill="#9E9E9E" />
                        </svg>

                        {phone || 'N/A'}
                    </small>
                    <small className="flex gap-3 items-center text-sm font-normal leading-[160%] text-black-label">
                        <svg width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 2C15 1.175 14.325 0.5 13.5 0.5H1.5C0.675 0.5 0 1.175 0 2V11C0 11.825 0.675 12.5 1.5 12.5H13.5C14.325 12.5 15 11.825 15 11V2ZM13.5 2L7.5 5.7425L1.5 2H13.5ZM13.5 11H1.5V3.5L7.5 7.25L13.5 3.5V11Z" fill="#9E9E9E" />
                        </svg>
                        {email || 'N/A'}
                    </small>
                </div>
            </div>
        </div>
    )
}

const ProductCard = ({ id, product, qty, currency, isDisabled, rate, cardType }: {
    product: string;
    qty: number;
    currency: string;
    id: string
    rate: number;
    currencyHave: string;
    isDisabled: boolean;
    cardType: 'multicurrency' | 'singlecurrency'
}) => {
    return (
        <Card className="p-4 w-full border rounded-xl relative">
            <div className="flex justify-between w-full">
                <div className="flex flex-col gap-3 w-full">
                    <div className="text-sm text-gray-700 w-full font-medium flex items-center justify-between">
                        <p className="">{product}</p>
                        <button disabled={isDisabled} className="disabled:opacity-40 right-4 text-blue-600">
                            <EditProduct currency={currency} quantity={qty} product_id={id} cardType={cardType}>
                                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.25781 4.53906H2.42448C1.98245 4.53906 1.55853 4.71466 1.24597 5.02722C0.933407 5.33978 0.757813 5.7637 0.757812 6.20573V13.7057C0.757813 14.1478 0.933407 14.5717 1.24597 14.8842C1.55853 15.1968 1.98245 15.3724 2.42448 15.3724H9.92448C10.3665 15.3724 10.7904 15.1968 11.103 14.8842C11.4156 14.5717 11.5911 14.1478 11.5911 13.7057V12.8724" stroke="#165FE3" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M10.7552 2.87769L13.2552 5.37769M14.4094 4.19853C14.7376 3.87032 14.922 3.42518 14.922 2.96103C14.922 2.49687 14.7376 2.05173 14.4094 1.72353C14.0812 1.39532 13.636 1.21094 13.1719 1.21094C12.7077 1.21094 12.2626 1.39532 11.9344 1.72353L4.92188 8.71103V11.211H7.42188L14.4094 4.19853Z" stroke="#165FE3" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </EditProduct>
                        </button>
                    </div>
                    <div className="text-xs text-gray-600 flex items-center gap-1">
                        <p className="uppercase">
                            {currency}
                        </p>
                        <p className="">
                            {qty}
                        </p>
                        <p className="">
                            <span className="text-blue-700">@</span> <small className="text-gray-500">INR</small> {rate}
                        </p>
                    </div>
                </div>
            </div>
        </Card>
    )
}

const DocumentsAndLRS = ({ documents, setOpen, kycStatus, documentsButtonState, activeCustomer, setDocIndex }: {
    documents: any;
    setOpen: (open: boolean) => void;
    documentsButtonState: {
        disabled: boolean;
        visible: boolean;
    },
    activeCustomer: any;
    setDocIndex: (index: number) => void;
    kycStatus?: string;
}) => {
    const [activeTab, setActiveTab] = useState<string>('documents');
    const [documentActiveTab, setDocumentActiveTab] = useState<string>('all');
    // set active tab as first tab from tabData
    const onTabChange = (value: string) => {
        setActiveTab(value);
    };

    const documentsColumns: ColumnDef<OrderDocumentType>[] = [
        {
            id: "doc_name",
            accessorKey: "doc_name",
            header: ({ column }) => (
                <DataTableColumnHeader className="whitespace-nowrap" column={column} title="Document Name" />
            ),
            cell: ({ row }) => {
                return (
                    <div className="flex items-center justify-between gap-1.5">
                        <p className="max-w-[200px] whitespace-normal break-words">
                            {row.getValue("doc_name")}
                        </p>
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            accessorKey: 'upload_date',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Upload Date" />
            ),
            cell: ({ row }) => {
                const dateStr = row.getValue("upload_date") as string;
                const [day, month, year] = dateStr.split("/").map(Number);

                // Create a Date object (month is 0-based)
                const date = new Date(year, month - 1, day);

                // Use Intl.DateTimeFormat
                const formatted = new Intl.DateTimeFormat("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                }).format(date);
                return (
                    <div className="flex items-center justify-between gap-1.5">
                        <p className="max-w-[200px] whitespace-normal break-words">
                            {/* get date as 27 Oct 2025 */}
                            {formatted}
                            {/* {row.getValue("upload_date")} */}
                        </p>
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            accessorKey: 'doc_status',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Status" />
            ),
            cell: ({ row }) => {
                const status = row.getValue("doc_status"); 
                if(row.original?.kyc_doc && kycStatus === 'active'){
                    return (
                        <div className="flex items-center gap-1.5">
                            <p className="max-w-[200px] bg-[#E1FFE2] px-2 rounded-md text-[#00A82D] whitespace-normal break-words">
                                Verified
                            </p>
                            <button
                                onClick={() => window.open(row.original.doc_url)}
                                className=""
                            >
                                <svg
                                    width="18"
                                    height="12"
                                    viewBox="0 0 18 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16.3221 5.28375C16.5501 5.60325 16.6641 5.76375 16.6641 6C16.6641 6.237 16.5501 6.39675 16.3221 6.71625C15.2976 8.15325 12.6808 11.25 9.16406 11.25C5.64656 11.25 3.03056 8.1525 2.00606 6.71625C1.77806 6.39675 1.66406 6.23625 1.66406 6C1.66406 5.763 1.77806 5.60325 2.00606 5.28375C3.03056 3.84675 5.64731 0.75 9.16406 0.75C12.6816 0.75 15.2976 3.8475 16.3221 5.28375Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M11.4141 6C11.4141 5.40326 11.177 4.83097 10.7551 4.40901C10.3331 3.98705 9.7608 3.75 9.16406 3.75C8.56733 3.75 7.99503 3.98705 7.57307 4.40901C7.15112 4.83097 6.91406 5.40326 6.91406 6C6.91406 6.59674 7.15112 7.16903 7.57307 7.59099C7.99503 8.01295 8.56733 8.25 9.16406 8.25C9.7608 8.25 10.3331 8.01295 10.7551 7.59099C11.177 7.16903 11.4141 6.59674 11.4141 6Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    );
                } 
                if(row.original?.kyc_doc && kycStatus !== 'active'){
                    return (
                        <div className="flex items-center gap-1.5">
                            <p className="max-w-[200px] bg-red-100 px-2 rounded-md text-red-500 whitespace-normal break-words">
                                Unverified
                            </p>
                            <button
                                onClick={() => window.open(row.original.doc_url)}
                                className=""
                            >
                                <svg
                                    width="18"
                                    height="12"
                                    viewBox="0 0 18 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16.3221 5.28375C16.5501 5.60325 16.6641 5.76375 16.6641 6C16.6641 6.237 16.5501 6.39675 16.3221 6.71625C15.2976 8.15325 12.6808 11.25 9.16406 11.25C5.64656 11.25 3.03056 8.1525 2.00606 6.71625C1.77806 6.39675 1.66406 6.23625 1.66406 6C1.66406 5.763 1.77806 5.60325 2.00606 5.28375C3.03056 3.84675 5.64731 0.75 9.16406 0.75C12.6816 0.75 15.2976 3.8475 16.3221 5.28375Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M11.4141 6C11.4141 5.40326 11.177 4.83097 10.7551 4.40901C10.3331 3.98705 9.7608 3.75 9.16406 3.75C8.56733 3.75 7.99503 3.98705 7.57307 4.40901C7.15112 4.83097 6.91406 5.40326 6.91406 6C6.91406 6.59674 7.15112 7.16903 7.57307 7.59099C7.99503 8.01295 8.56733 8.25 9.16406 8.25C9.7608 8.25 10.3331 8.01295 10.7551 7.59099C11.177 7.16903 11.4141 6.59674 11.4141 6Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    );
                } 
                if (status === "verified") {
                    return (
                        <div className="flex items-center gap-1.5">
                            <p className="max-w-[200px] bg-[#E1FFE2] px-2 rounded-md text-[#00A82D] whitespace-normal break-words">
                                Approved
                            </p>
                            <button
                                onClick={() => window.open(row.original.doc_url)}
                                className=""
                            >
                                <svg
                                    width="18"
                                    height="12"
                                    viewBox="0 0 18 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16.3221 5.28375C16.5501 5.60325 16.6641 5.76375 16.6641 6C16.6641 6.237 16.5501 6.39675 16.3221 6.71625C15.2976 8.15325 12.6808 11.25 9.16406 11.25C5.64656 11.25 3.03056 8.1525 2.00606 6.71625C1.77806 6.39675 1.66406 6.23625 1.66406 6C1.66406 5.763 1.77806 5.60325 2.00606 5.28375C3.03056 3.84675 5.64731 0.75 9.16406 0.75C12.6816 0.75 15.2976 3.8475 16.3221 5.28375Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M11.4141 6C11.4141 5.40326 11.177 4.83097 10.7551 4.40901C10.3331 3.98705 9.7608 3.75 9.16406 3.75C8.56733 3.75 7.99503 3.98705 7.57307 4.40901C7.15112 4.83097 6.91406 5.40326 6.91406 6C6.91406 6.59674 7.15112 7.16903 7.57307 7.59099C7.99503 8.01295 8.56733 8.25 9.16406 8.25C9.7608 8.25 10.3331 8.01295 10.7551 7.59099C11.177 7.16903 11.4141 6.59674 11.4141 6Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    );
                }
                if (status === "requested") {
                    return (
                        <Button disabled={documentsButtonState?.disabled} variant='ghost' onClick={() => { setOpen(true); setDocIndex(row.index) }} className=" w-full p-0 ">
                            <p className=" max-w-[200px] flex-1 flex justify-center items-center rounded-md p-2.5 border-black-label border gap-1.5">
                                Upload
                            </p>
                        </Button>
                    );
                }
                return (
                    <Button disabled={documentsButtonState?.disabled} variant='ghost' onClick={() => {                        
                        setOpen(true);
                        setDocIndex(row.index);

                    }} className=" w-full p-0 ">
                        <p className=" max-w-[200px] flex-1 flex justify-center items-center rounded-md p-2.5 border-black-label border gap-1.5">
                            Review
                        </p>
                    </Button>
                );
            },
            enableSorting: false,
        }
    ]

    const documentsColumnsMemoized = useMemo(() => documentsColumns, [documentsButtonState]);

    let documentsTabsdata = Object.keys(documents)?.map((tab, index) => {
        return {
            id: index,
            label: tab,
            value: tab,
            enabled: true
        };
    });

    const tableData = documents?.[documentActiveTab]
        ?.filter((doc: any) => doc?.created_by_cx === activeCustomer)
        ?.map((doc: any, index: number) => {
            // check if doc_name has pan, aadhaar, passport.

            const docNameLower = (doc?.doc_name || '').toLowerCase();
            const docNameCheckList = ['pan', 'aadhaar', 'passport'];
            const isKycDoc = docNameCheckList.some((name) => docNameLower.includes(name));

            if (isKycDoc) {
                return {
                    id: index,
                    doc_name: doc?.doc_name || 'N/A',
                    product: doc?.product || 'N/A',
                    upload_date: doc?.upload_date || new Date().toLocaleDateString(),
                    doc_status: kycStatus || 'N/A',
                    doc_url: doc?.document?.url || '',
                    kyc_doc: true,
                }
            }

            return {
                id: index,
                doc_name: doc?.doc_name || 'N/A',
                product: doc?.product || 'N/A',
                upload_date: doc?.upload_date || new Date().toLocaleDateString(),
                doc_status: doc?.status || 'N/A',
                doc_url: doc?.document?.url || '',
            }
        });

    const orderId = useParams().id as string;
    const queryCLient = useQueryClient();

    const { mutate: requestDocMutate } = useMutation({
        mutationFn: requestDocument,
        onSuccess: () => {
            queryCLient.invalidateQueries(['orderDetails'])
            toast.success('Document requested successfully');
        },
        onError: () => { }
    })

    const handleRequestDocument = (id: number | string) => {
        const matchedDoc = all_doc_List?.find(doc => doc.id === id);
        const type = matchedDoc?.type || 'personal';

        requestDocMutate({
            order_id: Number(orderId),
            req_doc: [
                {
                    doc_list_id: id,
                    type: type,
                    req_for: Number(activeCustomer),
                }
            ]
        });
    };

    return (
        <Tabs value={activeTab} onValueChange={onTabChange} defaultValue="customers" className="h-full w-full flex flex-col gap-8">
            <TabsContent value="documents">
                <Tabs value={documentActiveTab} onValueChange={setDocumentActiveTab} defaultValue="customers" className="h-full w-full flex flex-col gap-8">
                    <TabsList className=" hide-scrollbar w-full flex justify-start rounded-none gap-2 h-12">
                        {documentsTabsdata?.map((tab) => {
                            return (
                                <TabsTrigger
                                    key={tab.id}
                                    value={tab.value}
                                    disabled={!tab.enabled}
                                    className={`px-4 capitalize py-1 border border-[#E5E5E5] rounded-2xl text-sm text-[#999999] ${tab.value === documentActiveTab &&
                                        "font-semibold border-blue-700 text-blue-700"
                                        }`}
                                >
                                    {tab.label}
                                </TabsTrigger>
                            );
                        })}
                        <div className="flex gap-3 ml-auto">
                            <Button disabled={documentsButtonState?.disabled} variant='ghost' className="border flex gap-1.5 items-center border-blue-700 text-blue-700 rounded-lg" onClick={() => setOpen(true)}>
                                Upload Doc
                                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.55469 6.33333C4.55469 4.49238 6.04707 3 7.88802 3H12.5169C12.7531 3 12.9783 3.10027 13.1363 3.27587L16.4241 6.92898C16.5619 7.082 16.638 7.28058 16.638 7.48645V15.0833C16.638 16.9243 15.1456 18.4167 13.3047 18.4167H7.88802C6.04707 18.4167 4.55469 16.9243 4.55469 15.0833V6.33333Z" fill="#165FE3" />
                                    <path d="M16.6328 8V7.48645C16.6328 7.28058 16.5566 7.082 16.4189 6.92897L13.1311 3.27587C12.9731 3.10027 12.7479 3 12.5116 3H11.6328V4.66667C11.6328 6.50762 13.1252 8 14.9661 8H16.6328Z" fill="#98BDFF" />
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M13.522 12.0802C13.2706 12.3167 12.8751 12.3047 12.6385 12.0532L10.5938 9.87983L8.54892 12.0532C8.31242 12.3047 7.91692 12.3167 7.6655 12.0802C7.41409 11.8437 7.402 11.4482 7.63859 11.1967L9.886 8.80792C10.2723 8.39733 10.9153 8.39733 11.3015 8.80792L13.549 11.1967C13.7855 11.4482 13.7734 11.8437 13.522 12.0802Z" fill="white" />
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5938 16C10.2486 16 9.96875 15.7202 9.96875 15.375V9.125C9.96875 8.77983 10.2486 8.5 10.5938 8.5C10.9389 8.5 11.2188 8.77983 11.2188 9.125V15.375C11.2188 15.7202 10.9389 16 10.5938 16Z" fill="white" />
                                </svg>
                            </Button>
                            <RequestOrderDoc disabled={documentsButtonState?.disabled} onSubmit={handleRequestDocument} order_id={orderId} >
                                <Button variant='ghost' className="border flex gap-1.5 items-center border-blue-700 text-blue-700 rounded-lg">
                                    Request Doc
                                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.55469 6.33333C4.55469 4.49238 6.04707 3 7.88802 3H12.5169C12.7531 3 12.9783 3.10027 13.1363 3.27587L16.4241 6.92898C16.5619 7.082 16.638 7.28058 16.638 7.48645V15.0833C16.638 16.9243 15.1456 18.4167 13.3047 18.4167H7.88802C6.04707 18.4167 4.55469 16.9243 4.55469 15.0833V6.33333Z" fill="#165FE3" />
                                        <path d="M16.6328 8V7.48645C16.6328 7.28058 16.5566 7.082 16.4189 6.92897L13.1311 3.27587C12.9731 3.10027 12.7479 3 12.5116 3H11.6328V4.66667C11.6328 6.50762 13.1252 8 14.9661 8H16.6328Z" fill="#98BDFF" />
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.522 12.0802C13.2706 12.3167 12.8751 12.3047 12.6385 12.0532L10.5938 9.87983L8.54892 12.0532C8.31242 12.3047 7.91692 12.3167 7.6655 12.0802C7.41409 11.8437 7.402 11.4482 7.63859 11.1967L9.886 8.80792C10.2723 8.39733 10.9153 8.39733 11.3015 8.80792L13.549 11.1967C13.7855 11.4482 13.7734 11.8437 13.522 12.0802Z" fill="white" />
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5938 16C10.2486 16 9.96875 15.7202 9.96875 15.375V9.125C9.96875 8.77983 10.2486 8.5 10.5938 8.5C10.9389 8.5 11.2188 8.77983 11.2188 9.125V15.375C11.2188 15.7202 10.9389 16 10.5938 16Z" fill="white" />
                                    </svg>
                                </Button>
                            </RequestOrderDoc>
                        </div>
                    </TabsList>
                    <DataTable
                        columns={documentsColumnsMemoized}
                        data={tableData || []}
                        isToolbarVisible={false}
                        isPaginationVisible={false}
                        columnsBackground="#EEEEEE"
                    />
                </Tabs>
            </TabsContent>
        </Tabs>
    )
}

const KycDocumentsTable = ({ documents }: {
    documents: {id: number, created_at: string, status: string, customers_id: number, verified_by: number}[]
}) => {    

    const documentsColumns: ColumnDef<OrderDocumentType>[] = [
        // {
        //     id: "doc_name",
        //     accessorKey: "doc_name",
        //     header: ({ column }) => (
        //         <DataTableColumnHeader className="whitespace-nowrap" column={column} title="Document Name" />
        //     ),
        //     cell: ({ row }) => {
        //         return (
        //             <div className="flex items-center justify-between gap-1.5">
        //                 <p className="max-w-[200px] whitespace-normal break-words">
        //                     {row.getValue("doc_name")}
        //                 </p>
        //             </div>
        //         );
        //     },
        //     enableSorting: false,
        // },
        {
            accessorKey: 'upload_date',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Upload Date" />
            ),
            cell: ({ row }) => {
                return (
                    <div className="flex items-center justify-between gap-1.5">
                        <p className="max-w-[200px] whitespace-normal break-words">
                            {row.getValue("upload_date")}
                        </p>
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            accessorKey: 'doc_status',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Status" />
            ),
            cell: ({ row }) => {
                const labelColor = row.getValue("doc_status") === 'verified' ? 'bg-[#E1FFE2] text-[#00A82D]' :  'bg-[#FFF8F8] text-[#FF0000]';
                return(
                    <p className={`flex items-center gap-2 capitalize`}>
                        <p className={`p-1 px-3 rounded-lg ${labelColor}`}>
                            {row.getValue("doc_status")}    
                        </p>
                        {
                            row.original.doc_url &&
                            <div className="flex items-center gap-3 justify-center">
                                <Button onClick={()=>{
                                    window.open(row.original.doc_url)
                                }} variant='ghost' className=" w-full p-0 justify-start ">
                                    <Eye color="blue" className="h-4 w-4" />
                                </Button>
                                <Button onClick={async ()=> handleDownloadViaURL(row.original.doc_url, row.original.doc_name)} variant='ghost' className=" w-full p-0">
                                    <Download color="blue" className="h-4 w-4" />
                                </Button>
                            </div>
                        }
                    </p>
                )
            },
            enableSorting: false,
        }
    ]

    const documentsColumnsMemoized = useMemo(() => documentsColumns, []);    

    const tableData = documents?.map((doc, index: number) => {
            return {
                id: index,
                upload_date: new Date(doc?.created_at).toLocaleDateString('en-IN',{
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                }),
                doc_status: doc?.status || 'N/A',               
            }
        });

        console.log('tableData', tableData);

    return (
        <div className="w-full">
            <DataTable
                columns={documentsColumnsMemoized}
                data={tableData || []}
                isToolbarVisible={false}
                isPaginationVisible={false}
                columnsBackground="#EEEEEE"
            />
        </div>
    )
}